import React, { useState, useEffect } from 'react';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, Container, Box, Card, CardHeader, Avatar,
  CardContent, IconButton, TextField, FormControl, FormHelperText, useMediaQuery, useTheme, Typography, Grid, Chip
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Swal from 'sweetalert2';
import { message } from 'antd';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { CircularProgress } from '@mui/material';
import HttpsIcon from '@mui/icons-material/Https';

import NewClientDialog from '../Services/NewClientDialog';

function ViewClients() {
  const [clients, setClients] = useState([]);
  const [messageApi, contextHolder] = message.useMessage();
  const [openDiag, setOpenDiag] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [permissionError, setPermissionError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const checkPermissions = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      messageApi.error('No se encontró el token de autenticación.');
      setPermissionError(true);
      window.location.href = '/sign-in'; // Redirect to login if no token
      return;
    }
    setPermissionError(false);
    setIsLoading(true);
    try {
      const response = await fetch('https://dprincess-training.servehttp.com/users/check-modify-role-permissions', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.status === 403) {
        messageApi.error('No tienes permisos para acceder a esta sección.');
        setPermissionError(true);
        throw new Error('No tienes permisos para acceder a esta sección.');
      }
    } catch (error) {
      messageApi.error(error.message || 'Error al verificar permisos.');
      throw error; // Re-throw to handle it in the calling function
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkPermissions();
  }, []);

  // Responsive hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const token = localStorage.getItem('token');

  const requestClients = async () => {
    try {
      const response = await fetch('https://dprincess-training.servehttp.com/clients-list', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) {
        messageApi.open({
          type: 'error',
          content: 'Error al obtener la lista de clientes, la respuesta del servidor no fue la esperada, por favor refresque la página',
          duration: 5,
        });
      }
      const data = await response.json();
      return setClients(data);
    } catch (error) {
      console.error('Error:', error);
      messageApi.open({
        type: 'error',
        content: 'Hubo un error en el servidor al obtener la lista de clientes, por favor refresque la página',
        duration: 5,
      });
    }
  };

  const handleEditSubmit = async (values, { setSubmitting }) => {
    try {
      handleEditDialogClose();
      const confirmation = await Swal.fire({
        title: 'Guardar cambios',
        text: "¿Estas seguro/a de que quieres guardar los cambios?",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Sí, guardar cambios!',
        cancelButtonText: 'Cancelar',
      });
      if (confirmation.isConfirmed) {
        try {
          const response = await fetch(`https://dprincess-training.servehttp.com/clients/update-client/${values.clientID}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(values)
          });
          if (!response.ok) {
            messageApi.open({
              type: 'error',
              content: 'Error al actualizar el cliente',
              duration: 5,
            });
          } else {
            messageApi.open({
              type: 'success',
              content: 'Cliente actualizado correctamente',
              duration: 3,
            });
            requestClients();
            handleEditDialogClose();
          }
        } catch (error) {
          messageApi.open({
            type: 'error',
            content: 'Error en el servidor al actualizar el cliente',
            duration: 5,
          });
        }
        setSubmitting(false);
      }
      else {
        setSubmitting(false);
      }
    } catch (error) {
      messageApi.open({
        type: 'error',
        content: 'Error al guardar los cambios',
        duration: 5,
      });
      setSubmitting(false);
    }
  };

  const handleOpen = () => {
    setOpenDiag(true);
  };

  const handleClose = () => {
    setOpenDiag(false);
  };

  const handleClickOpen = (client) => {
    setSelectedClient(client);
    setEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedClient(null);
  };

  useEffect(() => {
    requestClients();
  }, []);

  // Mobile client component
  const MobileClientCard = ({ client }) => (
    <Card sx={{
      marginBottom: '1rem',
      border: '1px solid #e0e0e0',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(124, 58, 237, 0.1)',
      background: '#fff'
    }}>
      <CardContent sx={{ padding: '1rem' }}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
              <Typography variant="h6" sx={{
                color: '#5b21b6',
                fontWeight: 'bold',
                fontSize: '1.1rem',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}>
                <PersonIcon fontSize="small" />
                {client.clientName}
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleClickOpen(client)}
                sx={{
                  color: '#7c3aed',
                  background: '#ede9fe',
                  borderRadius: '8px',
                  '&:hover': {
                    background: '#f3e8ff',
                    color: '#f759bb'
                  }
                }}
              >
                <ModeEditIcon fontSize="small" />
              </IconButton>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              marginBottom: '0.5rem',
              color: '#666'
            }}>
              <EmailIcon fontSize="small" sx={{ color: '#7c3aed' }} />
              {client.clientEmail}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              fontSize: '0.875rem',
              color: '#666'
            }}>
              <PhoneIcon fontSize="small" sx={{ color: '#f759bb' }} />
              {client.cellphoneNumber}
            </Typography>
          </Grid>
          {client.phoneNumber && (
            <Grid item xs={6}>
              <Typography variant="body2" sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                fontSize: '0.875rem',
                color: '#666'
              }}>
                <PhoneIcon fontSize="small" sx={{ color: '#f759bb' }} />
                {client.phoneNumber}
              </Typography>
            </Grid>
          )}
          {client.address && (
            <Grid item xs={12}>
              <Typography variant="body2" sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                fontSize: '0.875rem',
                color: '#666',
                marginTop: '0.25rem'
              }}>
                <LocationOnIcon fontSize="small" sx={{ color: '#5b21b6' }} />
                {client.address}
              </Typography>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (permissionError) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2
        }}>
          <Avatar sx={{ bgcolor: '#f44336', width: 64, height: 64 }}>
            {/* Icono de candado para indicar acceso restringido */}
            <HttpsIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography variant="h5" sx={{ color: '#f44336', fontWeight: 700 }}>
            No tienes permiso para acceder a esta sección
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', textAlign: 'center' }}>
            Esta información solo está disponible para usuarios con permisos de administrador.
          </Typography>
        </Box>
      </Container>
    );
  };

  return (
    <Container
      maxWidth={false}
      sx={{
        borderRadius: { xs: '8px', md: '18px' },
        width: '100%',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: { xs: 2, md: 4 },
        mb: { xs: 2, md: 4 },
        px: { xs: 1, sm: 2, md: 3 }
      }}
    >
      {contextHolder}
      <Card
        sx={{
          width: '100%',
          maxWidth: { xs: '100%', md: 1200 },
          borderRadius: { xs: '12px', md: '18px' },
          boxShadow: '0 6px 24px 0 rgba(80, 0, 120, 0.10)',
          mt: { xs: 1, md: 3 },
          overflow: 'visible',
          border: '1.5px solid #e0e0e0',
          background: '#fff',
          mb: { xs: 2, md: 3 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            minHeight: { xs: '60px', md: '90px' },
            background: 'linear-gradient(90deg, #f3e8ff 0%, #fff 100%)',
            borderTopLeftRadius: { xs: '12px', md: '18px' },
            borderTopRightRadius: { xs: '12px', md: '18px' },
            borderBottom: '1.5px solid #ececec',
            flexDirection: { xs: 'column', sm: 'row' },
            padding: { xs: '1rem', sm: '0' },
            gap: { xs: 1, sm: 0 }
          }}
        >
          <CardHeader
            title={
              <span style={{
                fontSize: isMobile ? '1.5rem' : '2rem',
                fontWeight: 700,
                color: '#5b21b6',
                letterSpacing: '0.5px',
                marginLeft: isMobile ? '0' : '40px'
              }}>
                Clientes registrados
              </span>
            }
            subheader={
              <span style={{
                fontSize: isMobile ? '0.875rem' : '1rem',
                color: '#7c3aed',
                fontWeight: 400,
                opacity: 0.8,
                marginLeft: isMobile ? '0' : '40px'
              }}>
                {isMobile ? 'Lista de clientes' : 'Selecciona un cliente de la lista'}
              </span>
            }
            sx={{
              p: 0,
              m: 0,
              textAlign: { xs: 'center', sm: 'left' }
            }}
          />
          <Button
            variant='contained'
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => handleOpen()}
            sx={{
              height: { xs: '40px', md: '44px' },
              background: 'linear-gradient(90deg, #7c3aed 0%, #f759bb 100%)',
              color: '#fff',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '10px',
              fontSize: { xs: '0.875rem', md: '1rem' },
              marginRight: { xs: 0, sm: '40px' },
              boxShadow: '0 2px 8px 0 rgba(124, 58, 237, 0.10)',
              px: { xs: 2, md: 3 },
              '&:hover': {
                background: 'linear-gradient(90deg, #5b21b6 0%, #f759bb 100%)',
              },
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            {isMobile ? 'Agregar' : 'Agregar cliente'}
          </Button>
        </Box>

        <CardContent
          sx={{
            padding: { xs: '12px', md: '24px' },
            background: '#fafaff',
            borderBottomLeftRadius: { xs: '12px', md: '18px' },
            borderBottomRightRadius: { xs: '12px', md: '18px' },
          }}
        >
          {isMobile ? (
            <Box sx={{ width: '100%' }}>
              {clients.map((client) => (
                <MobileClientCard key={client.clientID} client={client} />
              ))}
              {clients.length === 0 && (
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '2rem',
                  textAlign: 'center'
                }}>
                  <PersonIcon sx={{ fontSize: '4rem', color: '#c4b5fd', marginBottom: '1rem' }} />
                  <Typography variant="h6" sx={{ color: '#7c3aed', marginBottom: '0.5rem' }}>
                    No hay clientes registrados
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    Agrega tu primer cliente usando el botón de arriba
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{
              width: '100%',
              overflowX: 'auto',
              '& .MuiDataGrid-root': {
                border: 'none',
                background: '#fff',
                borderRadius: '12px',
              },
              '& .MuiDataGrid-columnHeaders': {
                background: 'linear-gradient(90deg, #ede9fe 0%, #fff 100%)',
                color: '#5b21b6',
                fontWeight: 700,
                fontSize: '1.05rem',
                borderTopLeftRadius: '12px',
                borderTopRightRadius: '12px',
              },
              '& .MuiDataGrid-cell': {
                fontSize: '1rem',
                color: '#333',
                borderBottom: '1px solid #f3e8ff',
              },
              '& .MuiDataGrid-row': {
                background: '#fff',
                '&:hover': {
                  background: '#f3e8ff',
                }
              },
              '& .MuiDataGrid-footerContainer': {
                background: '#f3e8ff',
                borderBottomLeftRadius: '12px',
                borderBottomRightRadius: '12px',
              },
              '& .MuiDataGrid-toolbarContainer': {
                background: '#fafaff',
                borderRadius: '12px 12px 0 0',
                mb: 1,
              },
              '&::-webkit-scrollbar': {
                height: '8px',
                background: '#ede9fe',
                borderRadius: '8px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#c4b5fd',
                borderRadius: '8px',
              },
            }}>
              <DataGrid
                rows={clients}
                columns={[
                  { field: 'clientName', headerName: 'Nombre', flex: 1, minWidth: 120 },
                  { field: 'clientEmail', headerName: 'Email', flex: 1, minWidth: 150 },
                  { field: 'cellphoneNumber', headerName: 'Celular', flex: 0.8, minWidth: 110 },
                  { field: 'phoneNumber', headerName: 'Teléfono', flex: 0.8, minWidth: 110 },
                  { field: 'address', headerName: 'Dirección', flex: 1.5, minWidth: 180 },
                  {
                    field: 'opciones',
                    headerName: 'Editar',
                    flex: 0.3,
                    minWidth: 120,
                    sortable: false,
                    renderCell: (params) => (
                      <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                      }}>
                        <IconButton
                          sx={{
                            color: '#7c3aed',
                            background: '#ede9fe',
                            borderRadius: '8px',
                            p: 1,
                            '&:hover': {
                              background: '#f3e8ff',
                              color: '#f759bb'
                            }
                          }}
                          onClick={() => handleClickOpen(params.row)}
                        >
                          <ModeEditIcon />
                        </IconButton>
                      </div>
                    )
                  }
                ]}
                getRowId={(row) => row.clientID}
                autoHeight
                pageSize={5}
                rowsPerPageOptions={[5]}
                slots={{
                  toolbar: GridToolbar,
                }}
                slotProps={{
                  toolbar: {
                    showQuickFilter: true,
                    sx: {
                      padding: '8px',
                      background: '#fafaff',
                      borderRadius: '12px 12px 0 0'
                    }
                  }
                }}
                sx={{
                  border: 'none',
                  width: '100%',
                  marginBottom: '60px',
                  minWidth: 700,
                  maxWidth: '100%',
                  '& .MuiDataGrid-virtualScroller': {
                    overflowY: 'auto !important',
                    maxHeight: 'none !important',
                  },
                }}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Dialog para editar cliente */}
      <Dialog
        open={editDialogOpen}
        onClose={handleEditDialogClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, md: 4 },
            boxShadow: '0 8px 32px 0 rgba(80,0,120,0.18)',
            p: { xs: 0, md: 2 },
            background: 'linear-gradient(135deg, #fff 80%, #f3e8ff 100%)',
            margin: { xs: 0, md: '32px' },
            maxHeight: { xs: '100%', md: 'calc(100% - 64px)' }
          }
        }}
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: { xs: '1rem', md: '0' }
        }}>
          <DialogTitle
            sx={{
              fontWeight: 700,
              color: '#7c3aed',
              fontSize: { xs: '1.25rem', md: '1.5rem' },
              letterSpacing: '0.5px',
              pb: 0,
              padding: { xs: 0, md: 'inherit' }
            }}
          >
            Editar información del cliente
          </DialogTitle>
          {isMobile && (
            <IconButton
              edge="end"
              color="inherit"
              onClick={handleEditDialogClose}
              sx={{ color: '#7c3aed' }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
        <DialogContent sx={{
          padding: { xs: '1rem', md: '1.5rem' },
          '&:first-of-type': { paddingTop: { xs: '1rem', md: '1.5rem' } }
        }}>
          {selectedClient && (
            <Formik
              initialValues={{
                clientID: selectedClient.clientID,
                clientName: selectedClient.clientName || '',
                clientEmail: selectedClient.clientEmail || '',
                cellphoneNumber: selectedClient.cellphoneNumber || '',
                phoneNumber: selectedClient.phoneNumber || '',
                address: selectedClient.address || ''
              }}
              validationSchema={Yup.object({
                clientName: Yup.string().required('El nombre es requerido'),
                clientEmail: Yup.string().email('Correo inválido').required('El correo es requerido'),
                cellphoneNumber: Yup.string().required('El celular es requerido'),
                phoneNumber: Yup.string(),
                address: Yup.string()
              })}
              onSubmit={handleEditSubmit}
            >
              {formik => (
                <form onSubmit={formik.handleSubmit} style={{ marginTop: 8 }}>
                  <FormControl fullWidth margin="normal">
                    <TextField
                      label="Nombre"
                      name="clientName"
                      value={formik.values.clientName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.clientName && Boolean(formik.errors.clientName)}
                      variant="outlined"
                      size={isMobile ? "small" : "medium"}
                      sx={{ borderRadius: 2 }}
                    />
                    {formik.touched.clientName && formik.errors.clientName && (
                      <FormHelperText error>{formik.errors.clientName}</FormHelperText>
                    )}
                  </FormControl>
                  <FormControl fullWidth margin="normal">
                    <TextField
                      label="Correo electrónico"
                      name="clientEmail"
                      value={formik.values.clientEmail}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.clientEmail && Boolean(formik.errors.clientEmail)}
                      variant="outlined"
                      size={isMobile ? "small" : "medium"}
                      sx={{ borderRadius: 2 }}
                    />
                    {formik.touched.clientEmail && formik.errors.clientEmail && (
                      <FormHelperText error>{formik.errors.clientEmail}</FormHelperText>
                    )}
                  </FormControl>
                  <Grid container spacing={{ xs: 1, md: 2 }}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth margin="normal">
                        <TextField
                          label="Celular"
                          name="cellphoneNumber"
                          value={formik.values.cellphoneNumber}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.cellphoneNumber && Boolean(formik.errors.cellphoneNumber)}
                          variant="outlined"
                          size={isMobile ? "small" : "medium"}
                          sx={{ borderRadius: 2 }}
                        />
                        {formik.touched.cellphoneNumber && formik.errors.cellphoneNumber && (
                          <FormHelperText error>{formik.errors.cellphoneNumber}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth margin="normal">
                        <TextField
                          label="Teléfono"
                          name="phoneNumber"
                          value={formik.values.phoneNumber}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          variant="outlined"
                          size={isMobile ? "small" : "medium"}
                          sx={{ borderRadius: 2 }}
                        />
                      </FormControl>
                    </Grid>
                  </Grid>
                  <FormControl fullWidth margin="normal">
                    <TextField
                      label="Dirección"
                      name="address"
                      value={formik.values.address}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      variant="outlined"
                      size={isMobile ? "small" : "medium"}
                      sx={{ borderRadius: 2 }}
                    />
                  </FormControl>
                  <DialogActions sx={{
                    mt: 2,
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1, sm: 0 }
                  }}>
                    <Button
                      onClick={handleEditDialogClose}
                      sx={{
                        color: '#fff',
                        background: '#d32f2f',
                        borderRadius: 2,
                        px: 3,
                        fontWeight: 600,
                        '&:hover': { background: '#b71c1c' },
                        width: { xs: '100%', sm: 'auto' }
                      }}
                      size={isMobile ? "small" : "medium"}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      sx={{
                        color: '#fff',
                        background: '#f759bb',
                        borderRadius: 2,
                        px: 3,
                        fontWeight: 600,
                        boxShadow: '0 2px 8px 0 rgba(124, 58, 237, 0.10)',
                        '&:hover': {
                          background: ' #f759bb',
                        },
                        width: { xs: '100%', sm: 'auto' }
                      }}
                      disabled={formik.isSubmitting}
                      size={isMobile ? "small" : "medium"}
                    >
                      Guardar cambios
                    </Button>
                  </DialogActions>
                </form>
              )}
            </Formik>
          )}
        </DialogContent>
      </Dialog>

      <NewClientDialog openDiagTwo={openDiag} handleCloseTwo={handleClose} handleCancelTwo={handleClose} />

    </Container>
  )
}

export default ViewClients